import { memo } from 'preact/compat'
import { ChevronsDown } from 'lucide-preact'
import type { ScrollDepthBucketDto } from '@/types/analytics'
import { MetricBarList, type BarListItem } from './MetricBarList'

interface Props {
  funnel: ScrollDepthBucketDto[] | undefined
  className?: string
  bare?: boolean
  loading?: boolean
  error?: string | null
}

/**
 * The four discrete scroll-depth milestones (ORB-374), always rendered in 25→100 order with a
 * deepening bar colour so the drop-off reads as a funnel. Milestones absent from the payload
 * (no events) show as 0 — distinct from the per-session max scroll, so the two aren't conflated.
 */
const THRESHOLDS: { pct: number; barClass: string }[] = [
  { pct: 25, barClass: 'bg-gradient-to-r from-sky-400 to-sky-500' },
  { pct: 50, barClass: 'bg-gradient-to-r from-cyan-400 to-cyan-500' },
  { pct: 75, barClass: 'bg-gradient-to-r from-teal-400 to-teal-500' },
  { pct: 100, barClass: 'bg-gradient-to-r from-emerald-400 to-emerald-500' },
]

export const ScrollDepthFunnelTile = memo(({ funnel, className, bare, loading, error }: Props) => {
  // Index the payload by threshold so a milestone with no events still renders a 0 row.
  const countByThreshold = new Map<number, number>()
  for (const b of funnel ?? []) countByThreshold.set(b.ThresholdPct, b.Events?.Current ?? 0)

  // When no milestone has any events, fall back to the empty message (like the sibling
  // breakdown tiles) rather than a row of four zero bars. Once any data exists, show all
  // four milestones — including legitimate 0s — so the funnel reads consistently.
  const hasData = THRESHOLDS.some(t => (countByThreshold.get(t.pct) ?? 0) > 0)
  const items: BarListItem[] = hasData
    ? THRESHOLDS.map(t => ({
        label: `${t.pct}%`,
        value: countByThreshold.get(t.pct) ?? 0,
        barClass: t.barClass,
      }))
    : []

  return (
    <MetricBarList
      title="Scroll Depth"
      icon={<ChevronsDown className="w-5 h-5 text-primary-600 dark:text-primary-400" />}
      items={items}
      emptyMessage="No scroll data yet."
      maxItems={THRESHOLDS.length}
      className={className}
      bare={bare}
      dense
      loading={loading}
      error={error}
    />
  )
})
